// Copyright © 2017-2019  SDG Systems, LLC. All rights reserved.
// This file may not be used in any form without the express written permission of SDG Systems, LLC

<template>
  <v-container fluid grid-list-md>
    <v-card>
      <v-toolbar flat>
        <v-btn icon @click="exit"><v-icon>clear</v-icon></v-btn>
        <v-toolbar-title v-if="newUser">Add User</v-toolbar-title>
        <v-toolbar-title v-else>Edit User</v-toolbar-title>
        <v-spacer></v-spacer>
        <v-btn v-if="canModify" color="green" :disabled="newUser" @click.stop="showResetDialog = true">Reset Password</v-btn>
        <v-btn v-if="canModify" color="blue" @click="saveUser">Save</v-btn>
      </v-toolbar>
      <v-card-text>
      <v-layout row wrap>
        <v-flex xs12 md6>
          <v-text-field
            name="input"
            label="Name"
            v-model="user.name"
            required
            :disabled="!canModify"
          ></v-text-field>
        </v-flex>
        <v-flex xs12 md6>
          <v-text-field
            name="input"
            label="Username"
            :rules="[emailRule]"
            v-model="user.username"
            required
            :disabled="!canModify"
          ></v-text-field>
        </v-flex>
        <v-flex xs12 md6>
          <v-autocomplete
            v-bind:items="roles"
            v-model="user.roles"
            item-text="name"
            item-value="id"
            multiple
            chips
            label="User Roles"
            required
            :disabled="!canModify"
          ></v-autocomplete>
        </v-flex>
        <v-flex xs12 md6>
          <v-autocomplete
            v-bind:items="customers"
            v-model="user.securityCtx.customers"
            item-text="name"
            item-value="_id"
            multiple
            chips
            label="Customers"
            required
            :disabled="!canModify"
          ></v-autocomplete>
        </v-flex>
        <v-flex v-if="newUser" xs12 md6>
          <v-text-field
            name="input"
            label="Password"
            v-model="user.password"
            required
            :rules="[passwordRule]"
            type="password"
            counter :min="passwordLength">
          ></v-text-field>
        </v-flex>
      </v-layout>
    </v-card-text>
    </v-card>

    <password-reset v-model="showResetDialog" :user="user" :endpoint="endpoint"></password-reset>
    <error-snackbar v-model="errorText"></error-snackbar>
  </v-container>
</template>

<script>
import { mapGetters } from 'vuex'

import {PERMISSIONS, WILDCARDBSON} from 'vue-common/components/constants'
import ErrorSnackbar from 'vue-common/components/ErrorSnackbar'
import PasswordReset from 'vue-common/components/PasswordReset'
import {checkPermission} from 'vue-common/components/user'
import {getUserRolesText, getUserRoles} from 'vue-common/components/utils'

import {AXIOSAPI} from '@/api/api'
import ApiRequests from '@/api/ApiRequests'
import {ENDPOINT} from '@/shared/constants'
import Rules from 'vue-common/components/rules'

export default {
  components: {
    ErrorSnackbar,
    PasswordReset
  },

  data () {
    return {
      customers: [],
      endpoint: ENDPOINT,
      errorText: '',
      newUser: false,
      passwordLength: Rules.PASSWORDLENGTH,
      passwordReset1: '',
      passwordReset2: '',
      roles: [],
      showResetDialog: false,
      // User needs these empty fields or the field rules don't like it.
      user: {
        password: '',
        username: '',
        securityCtx: {
          customers: []
        }
      }
    }
  },

  created () {
    if (this.$route.params.id === 'new') {
      this.newUser = true
    }

    this.getUser()
    this.getRoles()

    ApiRequests.getCustomers().then(response => {
      this.customers = response.data
      if (this.allCustomers) {
        this.customers.unshift({name: 'All Customers', _id: WILDCARDBSON})
      }
    }).catch(e => {
      this.errorText = 'Failed to load customers'
    })
  },

  computed: {
    ...mapGetters({
      activeAccessRules: 'accessRules',
      activeUser: 'user',
      allCustomers: 'allCustomers',
      isSuperAdmin: 'isSuperAdmin',
      selectedSite: 'selectedSite'
    }),

    canModify: function () {
      return checkPermission(this.activeAccessRules, 'users', PERMISSIONS.MODIFY)
    },

    passwordsEqual: function () {
      if (this.passwordReset1 === this.passwordReset2) {
        return true
      } else {
        return 'Passwords are not the same'
      }
    },

    userRolesText: function () {
      return getUserRolesText(this.user.roles, this.roles)
    },

    userRoles: function () {
      var x = getUserRoles(this.user.roles, this.roles)
      return x
    }
  },

  methods: {
    getUser: function () {
      if (this.newUser) {
      } else {
        AXIOSAPI.get(ENDPOINT + 'users/' + this.$route.params.id).then((response) => {
          this.user = response.data
          if (!(this.user.securityCtx.sites)) {
            this.user.securityCtx.sites = []
          }
          if (!(this.user.securityCtx.companies)) {
            this.user.securityCtx.companies = []
          }
        })
      }
    },

    getRoles: function () {
      ApiRequests.getRoles(this.selectedSite._id).then((response) => {
        this.roles = response.data
        if (!this.isSuperAdmin) {
          var index = this.roles.findIndex((el) => el.name === 'Super Admin')
          if (index > -1) {
            this.roles.splice(index, 1)
          }
        }
      })
    },

    saveUser: function () {
      if (this.user.name &&
        (this.user.roles.length > 0) &&
        (this.emailRule(this.user.username) === true) &&
        ((Rules.password(this.user.password) === true) || !this.newUser)) {
        if (this.newUser) {
          AXIOSAPI.post(ENDPOINT + 'users', this.user).then(response => {
            this.exit()
          }).catch(e => {
            console.log(e)
            this.errorText = e.response.statusText + ': ' + e.response.data
          })
        } else {
          AXIOSAPI.put(ENDPOINT + 'users/' + this.$route.params.id, this.user).then(response => {
            if (this.user.id === this.activeUser.id) {
              AXIOSAPI.post('auth/logout')
            }
            this.exit()
          }).catch(e => {
            console.log(e)
            this.errorText = e.response.statusText + ': ' + e.response.data
          })
        }
      } else {
        this.errorText = 'Please completely fill out the form'
      }
    },

    emailRule: function (address) {
      var re = /^[A-z0-9._%+-]+@[A-z0-9.-]+\.[A-z.]{2,}$/
      if (re.test(address)) {
        return true
      } else {
        return 'Not a valid email address'
      }
    },

    passwordRule: function (password) {
      return Rules.password(password)
    },

    exit: function () {
      this.$router.push({name: 'users'})
    }
  }
}
</script>



// WEBPACK FOOTER //
// src/components/UserEdit.vue